/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { multiVariantStyles } from './themes/StyledDayButtonStyles.css';
import { styles as baseButtonStyles } from '../UNSAFE_BaseButton/themes/BaseButtonStyles.css';
import { classNames } from '../utils/UNSAFE_classNames';

type Props = {
  /**
   * Specifies that the button appears activated when the user activates the StyledDayButton.
   * When true, this adds the :active selector to the styling.
   * Combine this property with the other properties to get the styling you want.
   */
  isActivable?: boolean;
  /**
   * Specifies the styling of the button to look dimmed. 
   * Combine this property with the other properties to get the styling you want.
   */
  isDimmed?: boolean;
  /**
   * Specifies that the button appears hovered when the user hovers over the StyledDayButton.
   * When true, this adds the :hover selector to the styling.
   * Combine this property with the other properties to get the styling you want.
   */
  isHoverable?: boolean;
  /**
   * Specifies the styling of the button to look like a day that is adjacent to the current month.
   * Combine this property with the other properties to get the styling you want.
   */
  isAdjacentMonth?: boolean;
  /**
   * Specifies the styling of the button to look like it is restricted. 
   * Combine this property with the other properties to get the styling you want.
   */
  isRestricted?: boolean;
  /**
   * Specifies the styling of the button to look like it is selected. 
   * Combine this property with the other properties to get the styling you want.
   */
  isSelected?: boolean;
  /**
   * Specifies the styling of the button to look like it is today. 
   * Combine this property with the other properties to get the styling you want.
   */
  isToday?: boolean;
  /**
   * Specifies the label of the day button. This will be the day of the month.
   */
  label: string;
};

/**
 * StyledDayButton is a controlled component that displays a stylized span for a day that goes into a Calendar grid.
 */
const StyledDayButton = ({
  isActivable = false,
  isAdjacentMonth = false,
  isDimmed = false,
  isHoverable = false,
  isRestricted = false,
  isSelected = false,
  isToday = false,
  label
}: Props) => {
  // The multiVariantStyles function is defined in StyledDayButtonStyles.css.ts,
  // using the recipe utility provided by @vanilla-extract/recipes.
  // This function is used to merge the base styles, variants, and compound variants
  // into a single set of styles for text components based on the component's properties.
  const variantClasses = multiVariantStyles({
    active: isActivable ? 'isActivable' : 'notActiveable',
    dimmed: isDimmed ? 'isDimmed' : 'notDimmed',
    hover: isHoverable ? 'isHoverable' : 'notHoverable',
    adjacentMonth: isAdjacentMonth ? 'isAdjacentMonth' : 'notAdjacentMonth',
    restricted: isRestricted ? 'isRestricted' : 'notRestricted',
    selected: isSelected ? 'isSelected' : 'notSelected',
    today: isToday ? 'isToday' : 'notToday'
  });

  const classes = classNames([
    baseButtonStyles.base,
    baseButtonStyles.sizes.sm,
    baseButtonStyles.styled,
    variantClasses
  ]);
  return (
    <span
      class={classes}
      // tabIndex={isDisabled || !isTabbable ? -1 : 0} perhaps this will go on the outer span
    >
      {label}
    </span>
  );
};

export { StyledDayButton };
